import { expectDefined, immerable } from '@utils';
import type { Elements } from './Elements';
import { Graph } from './Graph';
import { isForest, topologicalSort } from './algo';
import type { MutableForest } from './interface/MutableForest';
import type { EID, EdgeDirection, Edges, NID, Nodes } from './types';

export type BlankForest = Forest<unknown, unknown, unknown>;

export class Forest<G, N, E> implements MutableForest<G, N, E> {
  [immerable] = true;

  private _inner: Graph<G, N, E>;

  constructor(opts?: { graph: Graph<G, N, E> } | { data?: G }) {
    if (opts !== undefined && 'graph' in opts) {
      const graph = opts.graph;
      if (!isForest(graph)) {
        throw new Error('Not a forest');
      }
      this._inner = graph;
    } else {
      this._inner = new Graph<G, N, E>(opts);
    }
  }

  // Get the graph of the forest.
  //
  // Note: This is for read-only traversal and analysis purposes. Do not mutate
  // the returned object.
  get graph(): Graph<G, N, E> {
    return this._inner;
  }

  //
  // VisitableData
  //

  get data(): G {
    return this._inner.data;
  }

  nodeData(id: NID): N {
    return this._inner.nodeData(id);
  }

  edgeData(id: EID): E {
    return this._inner.edgeData(id);
  }

  //
  // MutableData
  //

  setData(data: G): this {
    this._inner.setData(data);
    return this;
  }

  setNodeData(id: NID, data: N): this {
    this._inner.setNodeData(id, data);
    return this;
  }

  setEdgeData(id: EID, data: E): this {
    this._inner.setEdgeData(id, data);
    return this;
  }

  withData(f: (data: G) => void): this {
    this._inner.withData(f);
    return this;
  }

  withNodeData(id: NID, f: (data: N) => void): this {
    this._inner.withNodeData(id, f);
    return this;
  }

  withEdgeData(id: EID, f: (data: E) => void): this {
    this._inner.withEdgeData(id, f);
    return this;
  }

  //
  // VisitableGraph
  //

  get isEmpty(): boolean {
    return this._inner.isEmpty;
  }

  get nodeCount(): number {
    return this._inner.nodeCount;
  }

  get edgeCount(): number {
    return this._inner.edgeCount;
  }

  get nodes(): Nodes {
    return this._inner.nodes;
  }

  get edges(): Edges {
    return this._inner.edges;
  }

  get elements(): Elements {
    return this._inner.elements;
  }

  hasNode(id: NID): boolean {
    return this._inner.hasNode(id);
  }

  hasEdge(id: EID): boolean {
    return this._inner.hasEdge(id);
  }

  hasEdgeFromTo(source: NID, target: NID): boolean {
    return this._inner.hasEdgeFromTo(source, target);
  }

  hasEdgeBetween(a: NID, b: NID): boolean {
    return this._inner.hasEdgeBetween(a, b);
  }

  source(id: EID): NID {
    return this._inner.source(id);
  }

  target(id: EID): NID {
    return this._inner.target(id);
  }

  endpoints(id: EID): [NID, NID] {
    return this._inner.endpoints(id);
  }

  endpoint(id: EID, direction: EdgeDirection): NID {
    return this._inner.endpoint(id, direction);
  }

  outEdges(id: NID): Edges {
    return this._inner.outEdges(id);
  }

  inEdges(id: NID): Edges {
    return this._inner.inEdges(id);
  }

  incidentEdges(id: NID): Edges {
    return this._inner.incidentEdges(id);
  }

  outDegree(id: NID): number {
    return this._inner.outDegree(id);
  }

  inDegree(id: NID): number {
    return this._inner.inDegree(id);
  }

  degree(id: NID): number {
    return this._inner.degree(id);
  }

  successors(id: Nodes | NID): Nodes {
    return this._inner.successors(id);
  }

  transitiveSuccessors(id: Nodes | NID): Nodes {
    return this._inner.transitiveSuccessors(id);
  }

  predecessors(id: Nodes | NID): Nodes {
    return this._inner.predecessors(id);
  }

  transitivePredecessors(id: Nodes | NID): Nodes {
    return this._inner.transitivePredecessors(id);
  }

  neighbors(id: NID): Nodes {
    return this._inner.neighbors(id);
  }

  hasSuccessors(id: NID): boolean {
    return this._inner.hasSuccessors(id);
  }

  hasPredecessors(id: NID): boolean {
    return this._inner.hasPredecessors(id);
  }

  hasNeighbors(id: NID): boolean {
    return this._inner.hasNeighbors(id);
  }

  get roots(): Nodes {
    return this._inner.roots;
  }

  get leaves(): Nodes {
    return this._inner.leaves;
  }

  get nonRoots(): Nodes {
    return this._inner.nonRoots;
  }

  get nonLeaves(): Nodes {
    return this._inner.nonLeaves;
  }

  get internals(): Nodes {
    return this._inner.internals;
  }

  isRoot(id: NID): boolean {
    return this._inner.isRoot(id);
  }

  isLeaf(id: NID): boolean {
    return this._inner.isLeaf(id);
  }

  isInternal(id: NID): boolean {
    return this._inner.isInternal(id);
  }

  //
  // VisitableTreeish
  //

  inEdge(node: NID): EID | undefined {
    const inEdges = this.inEdges(node);
    if (inEdges.size === 0) {
      return undefined;
    }
    return inEdges.values().next().value;
  }

  parent(node: NID): NID | undefined {
    const inEdge = this.inEdge(node);
    if (inEdge === undefined) {
      return undefined;
    }
    return this.source(inEdge);
  }

  hasParent(node: NID): boolean {
    return this.parent(node) !== undefined;
  }

  children(node: NID): Nodes {
    return this.successors(node);
  }

  hasChildren(node: NID): boolean {
    return this.hasSuccessors(node);
  }

  childCount(node: NID): number {
    return this.outDegree(node);
  }

  descendants(node: NID): Nodes {
    return this.transitiveSuccessors(node);
  }

  hasDescendant(node: NID, descendant: NID): boolean {
    return this.descendants(node).has(descendant);
  }

  ancestors(node: NID): Nodes {
    return this.transitivePredecessors(node);
  }

  hasAncestor(node: NID, ancestor: NID): boolean {
    return this.ancestors(node).has(ancestor);
  }

  pathToRoot(node: NID): NID[] {
    const path = [];
    let current: NID | undefined = node;
    while (current !== undefined) {
      path.push(current);
      current = this.parent(current);
    }
    return path;
  }

  depth(node: NID): number {
    const path = this.pathToRoot(node);
    return path.length - 1;
  }

  /**
   * Finds the least common ancestor (LCA) of a subset of nodes in the forest.
   *
   * @param nodes - A subset of node IDs for which to find the LCA.
   * @returns The node ID of the least common ancestor or undefined if it doesn't exist.
   * @throws If the nodes set is empty or if any node does not exist in the forest.
   */
  leastCommonAncestor(nodes: Nodes): NID | undefined {
    // Ensure that the subset is not empty
    if (nodes.size === 0) {
      throw new Error('At least one node is required to find the least common ancestor.');
    }

    // Convert Nodes set to an array for easier handling
    const nodeArray = Array.from(nodes);

    // Check if all nodes exist in the forest
    for (const node of nodeArray) {
      if (!this.hasNode(node)) {
        throw new Error(`Node '${node}' does not exist in the forest.`);
      }
    }

    // If only one node is provided, return the node itself
    if (nodes.size === 1) {
      return nodeArray[0];
    }

    // Collect the path from each node to its root and reverse it to start from the root
    const paths: NID[][] = nodeArray.map(node => this.pathToRoot(node).reverse());

    // Check if all nodes are in the same tree by comparing their roots
    const roots = new Set(paths.map(path => path[0]));
    if (roots.size > 1) {
      // Nodes are in different trees; no common ancestor exists
      return undefined;
    }

    // Find the minimum length among all paths
    const minLength = Math.min(...paths.map(path => path.length));
    let lca: NID = paths[0][0]; // Initialize LCA as the root of the tree

    // Iterate over the paths to find the deepest common node
    for (let i = 0; i < minLength; i++) {
      const currentNodes = new Set(paths.map(path => path[i]));
      if (currentNodes.size === 1) {
        // All nodes at this level are the same; update LCA
        lca = paths[0][i];
      } else {
        // Nodes diverge at this level; stop searching
        break;
      }
    }

    return lca;
  }

  //
  // MutableForest
  //

  addNode(
    id: NID,
    opts?: // undefined: Add as root
    | { data?: N } // Add as root
      | { data?: N; parent: NID; newEdge: EID; edgeData?: E } // Add as child
  ): this {
    const { parent, data, newEdge, edgeData } = (opts ?? {}) as {
      parent?: NID;
      data?: N;
      newEdge?: EID;
      edgeData?: E;
    };
    if (parent === undefined) {
      // Add as root.
      this._inner.addNode(id, { data });
    } else {
      // Add as child.
      if (newEdge === undefined) {
        throw new Error('Missing new edge');
      }
      this._inner.addNode(id, { data });
      this._inner.addEdge(newEdge, parent, id, edgeData);
    }
    return this;
  }

  //
  // MutableTreeBase
  //

  removeNodeUngrouping(id: NID): this {
    const children = this.children(id);
    const newParent = this.parent(id);
    if (newParent === undefined) {
      // Children become roots. Delete all the edges from the node to its children.
      for (const child of children) {
        this._inner.removeEdge(expectDefined(this.inEdge(child)));
      }
    } else {
      // Move children to parent.
      for (const child of children) {
        this.moveNode(child, { newParent });
      }
    }
    this._inner.removeNode(id);
    return this;
  }

  removeNodeAndDescendants(id: NID): Nodes {
    return this._removeDescendants(new Set([id]));
  }

  removeDescendants(id: NID): Nodes {
    return this._removeDescendants(this.children(id));
  }

  private _removeDescendants(children: Nodes) {
    // Remove child nodes in reverse-topological sort order (most distant from the target first).
    const toRemove = topologicalSort(this, {
      roots: children,
      rootsOnly: true
    }).reverse();
    for (const node of toRemove) {
      this._inner.removeNode(node);
    }

    return new Set(toRemove);
  }

  moveNode(
    id: NID,
    opts?: { newParent: NID } | { newParent: NID; newEdge?: EID; edgeData?: E }
  ): this {
    const existingInEdge = this.inEdge(id);
    if (opts === undefined) {
      if (existingInEdge !== undefined) {
        this._inner.removeEdge(existingInEdge);
      }
    } else {
      const newParent = opts.newParent;
      const { newEdge, edgeData } = opts as { newEdge?: EID; edgeData?: E };
      if (existingInEdge === undefined) {
        // Moving root to be a child.
        if (newEdge === undefined) {
          throw new Error('Missing new edge');
        }
        this._inner.addEdge(newEdge, newParent, id, edgeData);
      } else {
        // Move to different parent.
        if (newEdge !== undefined) {
          throw new Error('Cannot change edge when moving a node');
        }
        this._inner.moveEdge(existingInEdge, { newSource: newParent });
      }
    }
    return this;
  }
}
